<template>
  <div class="domain-analysis">
    <div class="analysis-info">
      <div class="error-label" v-if="noData && showNoDataAlert">
        <i class="icon icon--bell-till"></i>
        <span>{{ $t('common.noCollectedData') }}</span>
        <button class="close-button" @click="showNoDataAlert = false">
          <i class="icon icon--close14"></i>
        </button>
      </div>
      <div class="log-date">
        {{ $t('journey.basedOn', { date: dataTimeString, timezone: timezoneOffset }) }}
      </div>
    </div>
    <div class="traffic-wrap">
      <div class="traffic-overview-wrap">
        <div class="box full-chart session-box">
          <p class="box-title"><i class="icon icon--ba-session"></i>Sessions</p>
          <div class="box-content" v-if="!loading.traffic">
            <div class="chart-info">
              <p class="total">
                <span>{{ chartData.session.total | thousand }}</span>
                <i
                  v-if="showSessionTooltip"
                  class="icon icon--info-yellow"
                  v-btf-tooltip="{ contents: sessionTooltipContents, size: 218, wordBreak: 'normal' }"
                ></i>
              </p>
              <p class="label">Session</p>
            </div>
            <div class="line-chart-wrap">
              <app-chart
                v-if="chartOptionsCache['session-line']"
                chartType="line"
                :chartOptions="chartOptionsCache['session-line']"
                :height="210"
                :useResize="true"
              >
              </app-chart>
            </div>
          </div>
          <i class="icon loading icon--dashboard-loading" v-else></i>
        </div>
        <div class="box full-chart pageview-box">
          <p class="box-title"><i class="icon icon--ba-pageview"></i>Page View</p>
          <div class="box-content" v-if="!loading.traffic">
            <div class="chart-info">
              <p class="total">{{ chartData.pageview.total | thousand }}</p>
              <p class="label">Page View</p>
            </div>
            <div class="line-chart-wrap">
              <app-chart
                v-if="chartOptionsCache['pageview-line']"
                chartType="line"
                :chartOptions="chartOptionsCache['pageview-line']"
                :height="210"
                :useResize="true"
              >
              </app-chart>
            </div>
          </div>
          <i class="icon loading icon--dashboard-loading" v-else></i>
        </div>
      </div>
      <div class="box report-box">
        <p class="box-title">
          <i class="icon icon--dashboard-mine-small"></i>
          <span>AI Custom Dashboard</span>
        </p>
        <i class="icon icon--gradient-blue"></i>
        <dashboard-ai v-if="isAllLoaded || domain.shared === 'shared'" :dashboard-data="domainData" :no-data="noData" :basic-info="basicInfo" />
        <i class="icon loading icon--dashboard-loading" v-else></i>
      </div>
      <div class="traffic-detail-wrap">
        <div class="box short full-chart">
          <p class="box-title">{{ $t('dashboard.analysis.chart.pvPerSession') }}</p>
          <template v-if="!loading.traffic">
            <p class="avg">
              <span class="avg-value">{{ chartData.pvPerSession.total | fixed(1) }}</span>
              <span class="avg-unit">Pages</span>
            </p>
            <div class="line-chart-wrap">
              <app-chart
                v-if="chartOptionsCache['pvPerSession-line']"
                chartType="line"
                :chartOptions="chartOptionsCache['pvPerSession-line']"
                :height="112"
                :useResize="true"
              >
              </app-chart>
            </div>
          </template>
          <i class="icon loading icon--dashboard-loading" v-else></i>
        </div>
        <div class="box short full-chart">
          <p class="box-title">{{ $t('dashboard.analysis.chart.intvlPerSession') }}</p>
          <template v-if="!loading.traffic">
            <p class="avg">
              <span class="avg-value">{{ getTimeString(chartData.intvlPerSession.total) }}</span>
            </p>
            <div class="line-chart-wrap">
              <app-chart
                v-if="chartOptionsCache['intvlPerSession-line']"
                chartType="line"
                :chartOptions="chartOptionsCache['intvlPerSession-line']"
                :height="112"
                :useResize="true"
              >
              </app-chart>
            </div>
          </template>
          <i class="icon loading icon--dashboard-loading" v-else></i>
        </div>
      </div>
    </div>
    <h3 class="title">{{ $t('dashboard.analysis.sessionSection') }}</h3>
    <div class="flex-equal-wrap">
      <div class="box map-box">
        <p class="box-title">{{ $t('dashboard.analysis.chart.country') }}</p>
        <div class="mode_selector">
          <button type="button" class="left" :class="{ on: mapMode === 'map' }"
            @click="mapMode = 'map'">
            <i class="icon icon--location" />Map
          </button>
          <button type="button" class="right" :class="{ on: mapMode === 'list' }"
            @click="mapMode = 'list'">
            <i class="icon dashboard--list" />List
          </button>
        </div>
        <div class="box-content" v-if="!loading.geo">
          <div class="chart-wrap" v-if="mapMode === 'map'">
            <app-chart
              v-if="geoChartOptions"
              chartType="map"
              :chartOptions="geoChartOptions"
              :height="316"
              :useResize="true"
            >
            </app-chart>
            <div class="zoom-control">
              <button type="button" class="btn_plus" @click="setMapZoom(true)">
                <span class="icon icon--plus"></span>
              </button>
              <button type="button" class="btn_minus" @click="setMapZoom(false)">
                <span class="icon icon--minus"></span>
              </button>
            </div>
          </div>
          <div class="geo-list" v-else>
            <div class="geo-list-header">
              <div class="rank">Rank</div>
              <div class="flag"></div>
              <div class="label">Country</div>
              <div class="unit">%</div>
            </div>
            <ul class="geo-list-body" v-if="geoListItems.length > 0">
              <li
                v-for="(item, index) in geoListItems"
                :key="item.countryFullname"
                :class="['item']"
              >
                <div class="rank">{{ item.country === 'etc' ? '-' : index + 1 }}</div>
                <i v-if="item.country === 'etc'" class="flag icon icon--etc-mark size-24"></i>
                <div v-else class="flag">
                  <country-flag
                    :countryCode="item.country"
                    :countryName="item.countryFullname"/>
                </div>
                <div class="label">{{ item.countryFullname }}</div>
                <div class="value">{{ item.percentage | fixed(1) }}%</div>
              </li>
            </ul>
            <p class="no-data" v-else>{{ $t('dashboard.analysis.noData') }}</p>
          </div>
        </div>
        <i class="icon loading icon--dashboard-loading" v-else></i>
        <div class="error-label" v-if="showGeoAlert && !loading.geo">
          <i class="icon icon--bell-till"></i>
          <span>{{ $t('dashboard.analysis.geoAlert') }}</span>
          <button class="close-button" @click="showGeoAlert = false">
            <i class="icon icon--close14"></i>
          </button>
        </div>
      </div>
      <div class="box">
        <p class="box-title">{{ $t('dashboard.analysis.chart.device') }}</p>
        <div class="box-content" v-if="!loading.device">
          <div class="chart-with-top">
            <div class="pie-chart-wrap">
              <app-chart
                v-if="chartOptionsCache['device-pie']"
                chartType="pie"
                class="pie_chart_container"
                :width="300"
                :height="170"
                :chartOptions="chartOptionsCache['device-pie']"
              >
              </app-chart>
            </div>
            <div class="top">
              <div class="top-header">
                <p class="rank"></p>
                <div class="category">
                  <p>{{ $t('dashboard.analysis.chart.deviceLabel') }}</p>
                  <p>%</p>
                </div>
              </div>
              <ul class="top-list">
                <template v-for="item in chartData.device.ratio || selectedInitRatio('device')">
                  <li
                    v-if="item.name !== 'etc'"
                    :key="item.name"
                    :class="[item.name, 'item']"
                  >
                    <span class="item-label">{{ $t(`dashboard.analysis.labelName.${item.name}`) || item.name }}</span>
                    <span class="item-value">{{ item.value | fixed(1) }}%</span>
                  </li>
                </template>
              </ul>
            </div>
          </div>
          <div class="line-chart-wrap">
            <app-chart
              v-if="chartOptionsCache['device-line']"
              chartType="line"
              :chartOptions="chartOptionsCache['device-line']"
              :height="140"
              :useResize="true"
            >
            </app-chart>
          </div>
        </div>
        <i class="icon loading icon--dashboard-loading" v-else></i>
      </div>
      <div class="box">
        <p class="box-title">{{ $t('dashboard.analysis.chart.exist') }}</p>
        <div class="box-content" v-if="!loading.exist">
          <div class="chart-with-top">
            <div class="pie-chart-wrap">
              <app-chart
                v-if="chartOptionsCache['exist-pie']"
                chartType="pie"
                class="pie_chart_container"
                :width="300"
                :height="170"
                :chartOptions="chartOptionsCache['exist-pie']"
              >
              </app-chart>
            </div>
            <div class="top">
              <div class="top-header">
                <p class="rank"></p>
                <div class="category">
                  <p>{{ $t('dashboard.analysis.chart.existLabel') }}</p>
                  <p>%</p>
                </div>
              </div>
              <ul class="top-list">
                <template v-for="item in chartData.exist.ratio || selectedInitRatio('exist')">
                  <li
                    v-if="item.name !== 'etc'"
                    :key="item.name"
                    :class="[item.name, 'item']"
                  >
                    <span class="item-label">{{ $t(`dashboard.analysis.labelName.${item.name}`) || item.name }}</span>
                    <span class="item-value">{{ item.value | fixed(1) }}%</span>
                  </li>
                </template>
              </ul>
            </div>
          </div>
          <div class="line-chart-wrap">
            <app-chart
              v-if="chartOptionsCache['exist-line']"
              chartType="line"
              :chartOptions="chartOptionsCache['exist-line']"
              :height="140"
              :useResize="true"
            >
            </app-chart>
          </div>
        </div>
        <i class="icon loading icon--dashboard-loading" v-else></i>
      </div>
    </div>
    <div class="box refer-box">
      <p class="box-title">{{ $t('dashboard.analysis.chart.referrer') }}</p>
      <div class="box-content" v-if="!loading.refer && !loading.device">
        <div class="pie-chart-wrap">
          <app-chart
            v-if="chartOptionsCache['refer-pie']"
            chartType="pie"
            class="pie_chart_container"
            :width="300"
            :height="170"
            :chartOptions="chartOptionsCache['refer-pie']"
          >
          </app-chart>
        </div>
        <div :class="['top', { 'segmented': journeyOptions.refer }]">
          <div class="top-header">
            <p class="rank">{{ $t('dashboard.analysis.chart.rank') }}</p>
            <div class="category">
              <p>{{ $t('dashboard.analysis.chart.referrerLabel') }}</p>
              <p>%</p>
            </div>
          </div>
          <ul class="top-list" v-if="chartData.refer.ratio">
            <template v-for="(item, idx) in chartData.refer.ratio">
              <li
                v-if="!journeyOptions.refer || item.name !== 'etc'"
                :key="idx"
                :class="[`ranking-${idx + 1}`, 'item', item.name === 'etc' && 'etc', {selected: referChartIndex === idx}]"
                :data-ranking="idx + 1"
                @click="changeReferChartIndex(idx)"
              >
                <span class="item-label" v-if="item.name !== 'etc'" :title="item.name">{{ item.name }}</span>
                <span class="item-label" v-else>{{ $t('dashboard.analysis.chart.etc') }}</span>
                <div class="item-value-wrap">
                  <span class="item-value">{{ item.value | fixed(1) }}%</span>
                  <i class="icon icon--arrow-body-right-red size-14" v-if="referChartIndex === idx"></i>
                  <i class="icon icon--arrow-body-dark-gray size-14" v-else></i>
                </div>
              </li>
            </template>
          </ul>
          <p class="no-data" v-else>{{ $t('dashboard.analysis.noData') }}</p>
        </div>
        <div class="line-chart-wrap">
          <app-chart
            v-if="chartOptionsCache['refer-line']"
            chartType="line"
            :chartOptions="chartOptionsCache['refer-line']"
            :height="266"
            :useResize="true"
          >
          </app-chart>
        </div>
      </div>
      <i class="icon loading icon--dashboard-loading" v-else></i>
    </div>
    <h3 class="title">{{ $t('dashboard.analysis.pvTopSection') }}</h3>
    <div class="flex-equal-wrap">
      <div v-for="(value, key) in domainData.pvTopData" :class="['box', 'short', 'pv-box']" :key="key">
        <p class="box-title">{{ $t(`ranking.pvSummary.${key}.title`) }}</p>
        <template v-if="!loading.pvTopData">
          <div class="avg-wrap">
            <span class="unit">Avg.</span>
            <div class="value-wrap">
              <p :class="`value ${key}`">
                <template v-if="key === 'interval'">
                  {{ getTimeString(value.avg) }}
                </template>
                <template v-else>
                  {{ value.avg | fixed(1) }} %
                </template>
              </p>
              <i :class="`icon icon--ranking-${key}`"></i>
            </div>
          </div>
          <div class="min-max-wrap">
            <p v-for="(item, index) in ['max', 'min']" :key="index" :class="`${item}`">
              <span class="label">{{ `${item}. ` }}</span>
              <template v-if="key === 'interval'">
                {{ getTimeString(value[item]) }}
              </template>
              <template v-else>
                {{ value[item] | fixed(1) }} %
              </template>
            </p>
          </div>
        </template>
        <i class="icon loading icon--dashboard-loading" v-else></i>
      </div>
    </div>
    <div class="box pv-top-list-box">
      <p class="box-title">{{ $t('dashboard.analysis.ranking.title') }}</p>
      <DomainRankingList v-if="!loading.pvTopList && domainData.pvTopList" :listData="domainData.pvTopList.slice(0, 20)" />
      <i class="icon loading icon--dashboard-loading" v-else></i>
     </div>
     <h3 class="title">{{ $t('dashboard.analysis.trend.title') }}</h3>
     <div class="box trend-box">
      <p class="box-title">{{ $t('dashboard.analysis.trend.subTitle') }}</p>
      <DomainTrendFunnel v-if="!loading.trend" :listData="domainData.trend" />
      <i class="icon loading icon--dashboard-loading" v-else></i>
     </div>
    <back-to-top :refName="'viewWrap'" />
  </div>
</template>
<script>
import { mapState } from 'vuex'
import { parse, format, addDays } from 'date-fns'
import { EventBus } from '@/libs/event-bus'
import { getTimeString, getPercentage, calculateProcessDateTime, getOffset, debounce, weekNumOfMonth, sum, simpleUrl } from '@/libs/utils'
import { dashboardChartOptions, SEGMENT_COLORS } from '@/libs/chart-option'
import { getTooltipHtml } from '@/libs/chart-helper'
import { getDeviceStatistics, getVisitStatistics, getGeoStatistics, getTopReferDashboard } from '@/api/journey'
import { getCardPvData, getCardSessionData, getListData } from '@/api/ranking'
import { getDashboardTrendData } from '@/api/trend'
import AppChart from '@/components/chart'
import CountryFlag from '@/components/ui/CountryFlag'
import BackToTop from '@/components/ui/BackToTop'
import DashboardAi from '@/components/dashboard/DashboardAi'
import DomainRankingList from '@/components/dashboard/DomainRankingList'
import DomainTrendFunnel from '@/components/dashboard/DomainTrendFunnel'

export default {
  name: 'DomainAnalysis',
  components: {
    AppChart,
    BackToTop,
    CountryFlag,
    DashboardAi,
    DomainRankingList,
    DomainTrendFunnel
  },
  props: {
  },
  data () {
    return {
      basicInfo: {},
      loading: {
        traffic: true,
        device: true,
        geo: true,
        exist: true,
        refer: true,
        pvTopData: true,
        pvTopList: true,
        trend: true
      },
      domainData: {},
      initData: {
        traffic: null,
        geo: [],
        exist: null,
        refer: null,
        pvTopData: {
          dropOff: { avg: 0, max: 0, min: 0 },
          reload: { avg: 0, max: 0, min: 0 },
          rollback: { avg: 0, max: 0, min: 0 },
          interval: { avg: 0, max: 0, min: 0 }
        },
        pvTopList: [],
        trend: []
      },
      initRatio: {
        device: [
          { name: 'desktop', value: 0 },
          { name: 'tablet', value: 0 },
          { name: 'phone', value: 0 }
        ],
        exist: [
          { name: 'new', value: 0 },
          { name: 'revisit', value: 0 }
        ]
      },
      mapMode: 'map',
      mapZoom: 1,
      geoStartDate: '2025-08-07',
      showGeoAlert: false,
      referChartIndex: 0,
      nextRequestId: 0,
      dashboardDataCache: {},
      chartOptionsCache: {},
      chartMeta: {
        session: {
          type: ['line']
        },
        pageview: {
          type: ['line']
        },
        pvPerSession: {
          type: ['line']
        },
        intvlPerSession: {
          type: ['line'],
          format: 'time'
        },
        device: {
          label: this.$t('dashboard.analysis.chart.deviceLabel'),
          type: ['pie', 'line'],
          format: 'percent',
          showTooltipLabel: true
        },
        geo: {
          type: ['map'],
          max: 10,
          min: 1
        },
        exist: {
          label: this.$t('dashboard.analysis.chart.existLabel'),
          type: ['pie', 'line'],
          format: 'percent',
          showTooltipLabel: true
        },
        refer: {
          label: this.$t('dashboard.analysis.chart.referrerLabel'),
          type: ['line', 'pie'],
          format: 'percent',
          showTooltipLabel: true
        }
      },
      noData: false,
      showNoDataAlert: false
    }
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'journeyPeriod',
      'domain',
      'isSampling'
    ]),
    dataTimeString () {
      if (this.domain) {
        const loadedAt = calculateProcessDateTime(this.domain)
        const formatText = this.$t('journey.basedOnFormat')
        return format(loadedAt, formatText)
      } else {
        return ''
      }
    },
    timezoneOffset () {
      return getOffset(this.domain.timezoneName)
    },
    isAllLoaded () {
      return !Object.values(this.loading).some(Boolean)
    },
    showSessionTooltip () {
      const sessionData = this.chartData.session.data.Session || []
      return this.getTotal(sessionData) !== this.chartData.session.total
    },
    sessionTooltipContents () {
      if (
        this.domain.samplingChangeDate &&
        new Date(this.journeyPeriod.start).getTime() < new Date(this.domain.samplingChangeDate).getTime()
      ) {
        return this.$t('dashboard.analysis.sessionTooltipSamplingChanged')
      }

      return this.$t('dashboard.analysis.sessionTooltip')
    },
    defaultDateRange () {
      const startDate = new Date(this.journeyPeriod.start)
      const endDate = new Date(this.journeyPeriod.end)
      const dates = []

      let current = new Date(startDate)

      while (current <= endDate) { // eslint-disable-line no-unmodified-loop-condition
        dates.push(current.toISOString().split('T')[0])
        current.setDate(current.getDate() + 1)
      }

      return dates
    },
    chartData () {
      let trafficLabels = this.defaultDateRange
      let deviceLabels = this.defaultDateRange
      let existLabels = this.defaultDateRange
      let referLabels = this.defaultDateRange

      let session = {}
      let pageview = {}
      let pvPerSession = {}
      let intvlPerSession = {}
      let deviceData = {}
      let deviceDailyTotal = {}
      let existData = {}
      let referData = {}

      let trafficDailyData = {}
      let deviceDailyData = {}
      let existDailyData = {}
      let referDailyData = {}

      let deviceRatio = null
      let existRatio = null
      let referRatio = null

      let sessionTotal = 0
      let pageviewTotal = 0
      let pvPerSessionTotal = 0
      let intvlPerSessionTotal = 0

      if (!this.noData) {
        if (this.domainData.traffic) {
          trafficDailyData = this.parseDailyData('session', this.domainData.traffic.daily)
          trafficLabels = Object.keys(trafficDailyData).length ? Object.keys(trafficDailyData) : this.defaultDateRange
          session['Session'] = Object.values(trafficDailyData).map(item => item.sessionSum || 0)
          pageview['PV'] = Object.values(trafficDailyData).map(item => item.totalPv || 0)
          pvPerSession['PV'] = Object.values(trafficDailyData).map(item => item.sessionSum ? (item.totalPv || 0) / item.sessionSum : 0)
          intvlPerSession['data'] = Object.values(trafficDailyData).map(item => {
            if (this.isGroupedGranularity) {
              return item.sessionSum ? (item.intervalSum || 0) / item.sessionSum : 0
            } else {
              return item.intvlPerSession || 0
            }
          })
          sessionTotal = this.domainData.traffic.total.sessionSum
          pageviewTotal = this.domainData.traffic.total.totalPv
          pvPerSessionTotal = this.domainData.traffic.total.pvPerSession
          intvlPerSessionTotal = this.domainData.traffic.total.intvlPerSession
        }
        if (this.basicInfo && this.basicInfo.session) {
          if (!this.journeyOptions.device || this.basicInfo.total[this.journeyOptions.device]) {
            deviceDailyData = this.parseDailyData('device', this.basicInfo.daily)
            if (this.journeyOptions.device) {
              deviceData = { [this.journeyOptions.device]: [] }
            } else {
              deviceData = { desktop: [], tablet: [], phone: [] }
            }
            const deviceTotal = this.basicInfo.session
            deviceLabels = Object.keys(deviceDailyData)
            deviceRatio = Object.entries(deviceData).map(([key, value]) => {
              const ratio = this.basicInfo.total[key] / deviceTotal * 100 || 0
              return { name: key, value: ratio }
            })
            if (this.journeyOptions.device) {
              deviceRatio.push({ name: 'etc', value: 100 - deviceRatio[0].value })
            }

            Object.entries(deviceDailyData).forEach(([date, devices]) => {
              const dailyTotal = devices.desktop + devices.tablet + devices.phone
              deviceDailyTotal[date] = this.journeyOptions.device ? devices[this.journeyOptions.device] : dailyTotal
              Object.keys(deviceData).forEach(key => {
                let value = 0
                if (devices[key] && dailyTotal) {
                  value = (devices[key] / dailyTotal * 100)
                }
                deviceData[key].push(value)
              })
            })
          }
        }
        if (this.domainData.exist) {
          existDailyData = this.parseDailyData('exist', this.domainData.exist.daily)
          if (this.journeyOptions.exist !== null) {
            existData = { [this.journeyOptions.exist ? 'revisit' : 'new']: [] }
          } else {
            existData = { new: [], revisit: [] }
          }
          const existTotal = this.getTotal(Object.values(this.domainData.exist.total))
          existLabels = Object.keys(existDailyData)
          existRatio = Object.entries(existData).map(([key, value]) => {
            const ratio = this.domainData.exist.total[key] / existTotal * 100 || 0
            return { name: key, value: ratio }
          })
          if (this.journeyOptions.exist !== null) {
            existRatio.push({ name: 'etc', value: 100 - existRatio[0].value })
          }

          Object.entries(existDailyData).forEach(([date, visits]) => {
            const dailyTotal = visits.new + visits.revisit
            Object.keys(existData).forEach(key => {
              let value = 0
              if (visits[key] && dailyTotal) {
                value = (visits[key] / dailyTotal * 100)
              }
              existData[key].push(value)
            })
          })
        }
        if (this.domainData.refer) {
          referDailyData = this.parseDailyData('refer', this.domainData.refer.daily)
          if (this.journeyOptions.refer) {
            referData = { [this.journeyOptions.refer]: [] }
          } else {
            referData = Object.fromEntries(
              this.domainData.refer.total.slice(0, 5).map(obj => [Object.keys(obj)[0], []])
            )
          }
          referLabels = Object.keys(referDailyData)
          const total = this.journeyOptions.device ? this.basicInfo.total[this.journeyOptions.device] : this.basicInfo.session

          let referEtcValue = total

          referRatio = Object.entries(referData).map(([key, value]) => {
            const item = this.domainData.refer.total.find(obj => obj.hasOwnProperty(key))
            const ratio = item[key] / total * 100 || 0
            referEtcValue -= item[key]
            return { name: key, value: ratio }
          })

          if (referEtcValue > 0) {
            referRatio.push({ name: 'etc', value: referEtcValue / total * 100 })
          }

          const referEtc = []
          Object.entries(referDailyData).forEach(([date, visits], index) => {
            const dailyTotal = deviceDailyTotal[date] || 0

            let etcValue = dailyTotal
            Object.keys(referData).forEach(key => {
              let value = 0
              let percent = 0
              if (visits[key]) value = visits[key]
              if (value && dailyTotal) {
                etcValue -= value
                percent = (value / dailyTotal * 100)
              }
              referData[key].push(percent)
            })

            referEtc.push(etcValue / dailyTotal * 100)
          })

          if (referEtcValue > 0) {
            referData['etc'] = referEtc
          }
        }
      }

      return {
        session: {
          dailyData: trafficDailyData,
          labels: trafficLabels,
          data: session,
          total: sessionTotal
        },
        pageview: {
          dailyData: trafficDailyData,
          labels: trafficLabels,
          data: pageview,
          total: pageviewTotal
        },
        pvPerSession: {
          dailyData: trafficDailyData,
          labels: trafficLabels,
          data: pvPerSession,
          total: pvPerSessionTotal
        },
        intvlPerSession: {
          dailyData: trafficDailyData,
          labels: trafficLabels,
          data: intvlPerSession,
          total: intvlPerSessionTotal
        },
        device: {
          dailyData: deviceDailyData,
          labels: deviceLabels,
          data: deviceData,
          ratio: deviceRatio
        },
        exist: {
          dailyData: existDailyData,
          labels: existLabels,
          data: existData,
          ratio: existRatio
        },
        refer: {
          dailyData: referDailyData,
          labels: referLabels,
          data: referData,
          ratio: referRatio
        }
      }
    },
    geoChartOptions () {
      if (!this.domainData.geo) return

      const total = this.domainData.geo.reduce((acc, item) => acc + item.count, 0)
      const seriesData = this.domainData.geo.map(item => {
        return {
          name: item.countryFullname,
          value: (item.count / total * 100).toFixed(1)
        }
      })
      return {
        series: [
          {
            ...dashboardChartOptions.mapChartSeries,
            zoom: this.mapZoom,
            data: seriesData
          }
        ],
        tooltip: {
          borderColor: 'transparent',
          formatter (param) {
            const content = [[param.name, `${param.value ? param.value : '0.0'}%`]]
            return getTooltipHtml(content)
          }
        },
        visualMap: {
          show: false
        }
      }
    },
    geoListItems () {
      const allItems = this.domainData.geo
      const total = this.domainData.geo.reduce((acc, item) => acc + item.count, 0)
      const topItems = allItems.slice(0, 5)

      const topTotal = sum(topItems, 'count')
      if (topTotal < total) {
        topItems.push({
          countryFullname: 'etc',
          country: 'etc',
          count: total - topTotal
        })
      }

      return topItems.map(item => {
        return {
          ...item,
          percentage: getPercentage(item.count, total)
        }
      })
    },
    isGroupedGranularity () {
      return ['month', 'week'].includes(this.journeyOptions.granularity)
    },
    cacheKey () {
      const aiRefererIds = [...(this.journeyOptions.aiRefererIds || [])].sort()
      return `${this.journeyOptions.device}_${this.journeyPeriod.start}_${this.journeyPeriod.end}_${this.journeyOptions.sid}_
      ${this.journeyOptions.subSid}_${this.journeyOptions.firstPath}_${this.journeyOptions.matchType}_
      ${this.journeyOptions.exist}_${this.journeyOptions.refer}_${JSON.stringify(aiRefererIds)}_
      ${JSON.stringify(this.journeyOptions.utm)}_${JSON.stringify(this.journeyOptions.survey)}_
      ${this.journeyOptions.granularity}_${this.isGroupedGranularity ? 'day' : this.journeyOptions.granularity}`
    }
  },
  watch: {
    domain (value) {
      if (value) {
        this.refreshData()
      }
    },
    'journeyOptions' () {
      this.refreshData()
    },
    'journeyPeriod' () {
      if (new Date(this.journeyPeriod.start) < new Date(this.geoStartDate)) {
        this.showGeoAlert = true
      } else {
        this.showGeoAlert = false
      }
      this.refreshData()
    }
  },
  created () {
    this.debouncedLoadData = debounce(this.loadData, 300)
    this.refreshData()
  },
  methods: {
    refreshData () {
      this.prepareLoadData()
      this.debouncedLoadData()
    },
    prepareLoadData () {
      this.nextRequestId++
      this.basicInfo = {}
      this.domainData = JSON.parse(JSON.stringify(this.initData))
      this.referChartIndex = 0
      this.mapZoom = 1
      this.mapMode = 'map'
      this.noData = false
      this.showNoDataAlert = false
      Object.keys(this.loading).forEach(key => {
        this.loading[key] = true
      })
    },
    parseDailyData (type, rawData) {
      if (this.isGroupedGranularity) {
        const tempData = {}
        const getLabelDt = (dt) => {
          if (this.journeyOptions.granularity === 'month') {
            return format(parse(dt).setDate(1), 'YYYY-MM-DD')
          }
          if (this.journeyOptions.granularity === 'week') {
            return weekNumOfMonth(new Date(dt))
          }
          return dt
        }
        Object.entries(rawData).forEach(([dt, value]) => {
          const dailyValue = type === 'refer'
            ? value.reduce((acc, obj) => ({ ...acc, ...obj }), {})
            : value
          const labelDt = getLabelDt(dt)
          if (!tempData[labelDt]) {
            tempData[labelDt] = { ...dailyValue, dt }
          } else {
            for (const key in dailyValue) {
              if (key !== 'dt') {
                tempData[labelDt][key] = (tempData[labelDt][key] || 0) + dailyValue[key]
              }
            }
          }
        })

        const labelDts = Object.keys(tempData).sort()
        labelDts.forEach((label, idx) => {
          const curr = tempData[label]
          const nextLabelDt = labelDts[idx + 1]
          curr.endDt = nextLabelDt ? format(addDays(parse(tempData[nextLabelDt].dt), -1), 'YYYY-MM-DD') : this.journeyPeriod.end
        })
        return tempData
      } else {
        if (type === 'refer') {
          return Object.fromEntries(
            Object.entries(rawData).map(([date, arr]) => [
              date,
              arr.reduce((acc, obj) => ({ ...acc, ...obj }), {})
            ])
          )
        }
      }
      return rawData
    },
    getTotal (arr) {
      return arr.reduce((acc, curr) => acc + curr, 0)
    },
    changeReferChartIndex (index) {
      if (this.referChartIndex === index) return
      this.referChartIndex = index
      this.chartOptionsCache[`refer-line`] = this.getLineChartOptions('refer', index)
    },
    updateChartOptions (chartType, index = null) {
      const chartTypes = this.chartMeta[chartType].type
      chartTypes.forEach((type) => {
        if (type === 'line') {
          this.chartOptionsCache[`${chartType}-${type}`] = this.getLineChartOptions(chartType, index)
        } else if (type === 'pie') {
          this.chartOptionsCache[`${chartType}-${type}`] = this.getPieChartOptions(chartType)
        }
      })
    },
    isNotEmpty (obj) {
      return obj && Object.keys(obj).length > 0
    },
    cloneData (data) {
      return JSON.parse(JSON.stringify(data))
    },
    applyDashboardCache (cachedData) {
      this.basicInfo = this.cloneData(cachedData.basicInfo)
      this.domainData = this.cloneData(cachedData.domainData)
      this.noData = cachedData.noData
      this.showNoDataAlert = cachedData.showNoDataAlert
      this.updateAllCharts()
      EventBus.$emit('segmentNoData', cachedData.segmentNoData)
      Object.keys(this.loading).forEach(key => {
        this.loading[key] = false
      })
    },
    saveDashboardCache (cacheKey, segmentNoData) {
      this.dashboardDataCache[cacheKey] = {
        basicInfo: this.cloneData(this.basicInfo),
        domainData: this.cloneData(this.domainData),
        noData: this.noData,
        showNoDataAlert: this.showNoDataAlert,
        segmentNoData
      }
    },
    loadData () {
      const requestId = this.nextRequestId
      const cacheKey = this.cacheKey

      if (this.dashboardDataCache[cacheKey]) {
        this.applyDashboardCache(this.dashboardDataCache[cacheKey])
        return
      }

      const params = {
        ...this.journeyOptions,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        granularity: this.isGroupedGranularity ? 'day' : this.journeyOptions.granularity
      }

      Promise.all([
        getDeviceStatistics({ ...params, device: null, refer: null, exist: null }, true),
        getCardSessionData(params, true)
      ]).then(([deviceRes, sessionRes]) => {
        if (requestId !== this.nextRequestId) return
        const deviceNoData = !this.journeyOptions.device ? !deviceRes.session : !deviceRes.total[this.journeyOptions.device]
        const segmentNoData = !sessionRes.total.sessionSum
        this.noData = deviceNoData || segmentNoData
        EventBus.$emit('segmentNoData', segmentNoData)

        if (!this.noData) {
          this.basicInfo = deviceRes
          this.domainData.traffic = {
            total: sessionRes.total || {},
            daily: sessionRes.daily || {}
          }
          this.loading.traffic = false
          this.loading.device = false
          this.updateChartOptions('session')
          this.updateChartOptions('pageview')
          this.updateChartOptions('pvPerSession')
          this.updateChartOptions('intvlPerSession')
          this.updateChartOptions('device')

          const detailRequests = []

          if (this.journeyPeriod.end >= this.geoStartDate) {
            const geoParams = { ...params }
            if (this.journeyPeriod.start < this.geoStartDate) {
              geoParams.start = this.geoStartDate
            }
            detailRequests.push(getGeoStatistics(geoParams).then(res => {
              if (requestId !== this.nextRequestId) return
              this.domainData.geo = res.geo
            }).catch(() => {}).finally(() => {
              if (requestId !== this.nextRequestId) return
              this.loading.geo = false
            }))
          } else {
            this.loading.geo = false
          }

          detailRequests.push(getVisitStatistics({ ...params, exist: null }, true).then(res => {
            if (requestId !== this.nextRequestId) return
            if (this.isNotEmpty(res.total) && this.isNotEmpty(res.daily)) {
              this.domainData.exist = res
            }
          }).catch(() => {}).finally(() => {
            if (requestId !== this.nextRequestId) return
            this.updateChartOptions('exist')
            this.loading.exist = false
          }))

          detailRequests.push(getTopReferDashboard({ ...params, refer: null, exist: null }, true).then(res => {
            if (requestId !== this.nextRequestId) return
            if (this.isNotEmpty(res.total) && this.isNotEmpty(res.daily)) {
              this.domainData.refer = res
            }
          }).catch(() => {}).finally(() => {
            if (requestId !== this.nextRequestId) return
            this.updateChartOptions('refer', this.referChartIndex)
            this.loading.refer = false
          }))

          detailRequests.push(getCardPvData(params, true).then(res => {
            if (requestId !== this.nextRequestId) return
            const data = res.cardData.data
            Object.entries(data).forEach(([key, value]) => {
              if (!this.domainData.pvTopData[key]) return

              let avg = value.avgTop20
              let min = 0
              let max = 0
              const getTop20Value = item => key === 'interval' ? item.interval : item.percent

              if (value.top20.length) {
                min = value.top20.reduce((min, curr) => Math.min(min, getTop20Value(curr)), getTop20Value(value.top20[0]))
                max = value.top20.reduce((max, curr) => Math.max(max, getTop20Value(curr)), getTop20Value(value.top20[0]))
              }

              this.domainData.pvTopData[key] = {
                avg,
                max,
                min
              }
            })
          }).catch(() => {}).finally(() => {
            if (requestId !== this.nextRequestId) return
            this.loading.pvTopData = false
          }))

          detailRequests.push(getListData({ ...params, pageId: 0, pageSize: 60, sortBy: '-pv' }, true).then(res => {
            if (requestId !== this.nextRequestId) return
            this.domainData.pvTopList = res
            this.domainData.pvTopList.forEach(item => {
              item.listUrl = simpleUrl(item.url, this.domain.matchType, this.domain.matchType !== 'simple')
            })
          }).catch(() => {}).finally(() => {
            if (requestId !== this.nextRequestId) return
            this.loading.pvTopList = false
          }))

          detailRequests.push(getDashboardTrendData(params, true).then(res => {
            if (requestId !== this.nextRequestId) return
            this.domainData.trend = res.seqStats
          }).catch(() => {}).finally(() => {
            if (requestId !== this.nextRequestId) return
            this.loading.trend = false
          }))

          Promise.allSettled(detailRequests).then(() => {
            if (requestId !== this.nextRequestId) return
            this.saveDashboardCache(cacheKey, segmentNoData)
          })
        } else {
          if (requestId !== this.nextRequestId) return
          this.showNoDataAlert = true
          this.updateAllCharts()
          Object.keys(this.loading).forEach(key => {
            this.loading[key] = false
          })
          this.saveDashboardCache(cacheKey, segmentNoData)
        }
      }).catch(() => {
        if (requestId !== this.nextRequestId) return
        this.updateAllCharts()
        Object.keys(this.loading).forEach(key => {
          this.loading[key] = false
        })
      })
    },
    updateAllCharts () {
      this.updateChartOptions('session')
      this.updateChartOptions('pageview')
      this.updateChartOptions('pvPerSession')
      this.updateChartOptions('intvlPerSession')
      this.updateChartOptions('device')
      this.updateChartOptions('exist')
      this.updateChartOptions('refer', this.referChartIndex)
    },
    getPieChartOptions (chartType) {
      let color = dashboardChartOptions.color[chartType].pieBg
      if (this.journeyOptions[chartType] !== null) {
        let selected = this.journeyOptions[chartType]
        if (chartType === 'exist') {
          selected = selected ? 'revisit' : 'new'
        } else if (chartType === 'refer') {
          selected = 'refer'
        }
        color = [SEGMENT_COLORS[selected].pie, SEGMENT_COLORS.etc]
      }

      const series = this.chartData[chartType].ratio ? this.chartData[chartType].ratio.map(item => {
        const disableEmphasis = this.journeyOptions[chartType] !== null && item.name === 'etc'
        return {
          ...item,
          emphasis: {
            scale: !disableEmphasis,
            itemStyle: {
              color: disableEmphasis ? SEGMENT_COLORS.etc : null
            }
          }
        }
      }) : null

      return {
        series: [
          {
            ...dashboardChartOptions.pieChartSeries,
            data: series
          }
        ],
        tooltip: {
          trigger: 'item',
          borderWidth: 0,
          formatter: (param) => {
            if (this.journeyOptions[chartType] !== null && param.name === 'etc') {
              return null
            }
            const nameKey = `dashboard.analysis.labelName.${param.name}`
            const name = this.$i18n.te(nameKey) ? this.$t(nameKey) : param.name
            let value = param.value.toFixed(1) + '%'

            const content = [
              `${param.marker}${name} :`,
              `${value}`
            ]
            const title = this.chartMeta[chartType].label
            return getTooltipHtml([content], title)
          }
        },
        color: color
      }
    },
    getLineChartOptions (chartType, dataIndex = null) {
      if (!this.chartData[chartType]) return

      let chartData = {}

      if (dataIndex !== null && this.isNotEmpty(this.chartData[chartType].data)) {
        const name = Object.keys(this.chartData[chartType].data)[dataIndex]
        chartData = { [name]: this.chartData[chartType].data[name] }
      } else {
        chartData = this.chartData[chartType].data
      }

      let lineColor = null
      let areaColor = null
      if (chartType in this.journeyOptions && this.journeyOptions[chartType] !== null) {
        let selected = this.journeyOptions[chartType]
        if (chartType === 'exist') {
          selected = selected ? 'revisit' : 'new'
        } else if (chartType === 'refer') {
          selected = 'refer'
        }

        lineColor = SEGMENT_COLORS[selected].line
        areaColor = SEGMENT_COLORS[selected].area
      }

      const series = Object.entries(chartData).map(([name, data], idx) => {
        if (this.chartMeta[chartType].format === 'time') {
          data = data.map(item => item < 500 ? 0 : item)
        } else {
          data = data.map(item => item < 0.05 ? 0 : item)
        }
        const index = dataIndex !== null ? dataIndex : idx
        return {
          name,
          data,
          ...dashboardChartOptions.lineChartSeries,
          showSymbol: data.length === 1,
          areaStyle: {
            color: areaColor || dashboardChartOptions.color[chartType].lineAreaBg[index]
          },
          lineStyle: {
            color: lineColor || dashboardChartOptions.color[chartType].lineBg[index]
          },
          emphasis: {
            itemStyle: {
              borderColor: lineColor || dashboardChartOptions.color[chartType].lineBg[index]
            }
          }
        }
      })

      const isEmpty = !series.length

      if (isEmpty) {
        series.push({
          ...dashboardChartOptions.lineChartSeries,
          lineStyle: { width: 0 },
          data: [0, 0, 0, 0, 0, 0, 0]
        })
      }

      return {
        ...dashboardChartOptions.lineChart,
        series,
        xAxis: {
          ...dashboardChartOptions.lineChart.xAxis,
          data: this.chartData[chartType].labels,
          axisLabel: {
            ...dashboardChartOptions.lineChart.xAxis.axisLabel,
            formatter: (value) => {
              if (this.journeyOptions.granularity === 'month') {
                return format(value, 'YY-MM')
              } else if (this.journeyOptions.granularity === 'week') {
                return value
              } else if (this.journeyOptions.granularity === 'hour') {
                return format(value, 'M/D HH’')
              } else {
                return format(value, 'M/D')
              }
            }
          }
        },
        yAxis: {
          ...dashboardChartOptions.lineChart.yAxis,
          show: true,
          name: chartType.indexOf('PerSession') > -1 ? this.$t(`dashboard.analysis.yAxis.${chartType}`) : undefined,
          min: this.chartMeta[chartType].format === 'percent' ? 0 : undefined,
          max: this.chartMeta[chartType].format === 'percent' ? 100 : undefined,
          interval: this.chartMeta[chartType].format === 'percent' ? chartType === 'refer' ? 25 : 50 : undefined,
          axisLabel: {
            ...dashboardChartOptions.lineChart.yAxis.axisLabel,
            formatter: (value) => {
              if (value === 0) {
                return '0'
              } else if (this.chartMeta[chartType].format === 'percent') {
                return value + '%'
              } else if (isEmpty) {
                return ''
              } else if (this.chartMeta[chartType].format === 'time') {
                const time = this.getTimeString(value)
                const timeArr = time.split(' ')
                return timeArr.length > 2 ? `${timeArr[0]} ${timeArr[1]}` : time
              } else if (!isNaN(value)) {
                return value.toLocaleString()
              } else {
                return value
              }
            }
          }
        },
        grid: {
          ...dashboardChartOptions.lineChart.grid,
          top: chartType.indexOf('PerSession') > -1 ? 30 : 10
        },
        tooltip: isEmpty ? null : {
          ...dashboardChartOptions.lineChart.tooltip,
          formatter: (params) => {
            const content = []

            for (let i = 0; i < params.length; i++) {
              const param = params[i]
              let value = param.data
              if (this.chartMeta[chartType].format === 'percent') {
                value = param.data.toFixed(1) + '%'
              } else if (this.chartMeta[chartType].format === 'time') {
                value = this.getTimeString(param.data)
              } else if (!isNaN(param.data)) {
                value = (Math.round(param.data * 10) / 10).toLocaleString()
              }
              const nameKey = `dashboard.analysis.labelName.${param.seriesName}`
              let name = this.$i18n.te(nameKey) ? this.$t(nameKey) : param.seriesName

              if (this.chartMeta[chartType].showTooltipLabel) {
                const contentItem = [`${param.marker}${name} :`, `${value}`]
                if (name === 'etc') {
                  contentItem[0] = `${param.marker}${this.$t('dashboard.analysis.chart.etc')} :`
                  contentItem.push('etc')
                }
                content.push(contentItem)
              } else {
                content.push([value, name])
              }
            }
            let title = ''
            if (['month', 'week'].indexOf(this.journeyOptions.granularity) > -1) {
              const dailyItem = this.chartData[chartType].dailyData[params[0].axisValue]
              title = dailyItem.dt + ' ~ ' + dailyItem.endDt
            } else if (this.journeyOptions.granularity === 'hour') {
              title = format(params[0].axisValue, 'YY-MM-DD HH’')
            } else {
              title = format(params[0].axisValue, 'YY-MM-DD')
            }
            return getTooltipHtml(content, title, params.length === 1 ? 'single' : 'multi')
          }
        },
        color: lineColor || (dataIndex !== null ? dashboardChartOptions.color[chartType].lineBg[dataIndex] : dashboardChartOptions.color[chartType].lineBg)
      }
    },
    setMapZoom (isPlus) {
      const step = isPlus ? 1 : -1
      const zoom = this.mapZoom + step

      if (zoom > this.chartMeta.geo.max || zoom < this.chartMeta.geo.min) {
        return
      }
      this.mapZoom = zoom
    },
    getTimeString (value) {
      return getTimeString(value / 1000)
    },
    selectedInitRatio (type) {
      let selectedSegment = this.journeyOptions[type]
      if (selectedSegment === null) {
        return this.initRatio[type]
      } else {
        if (type === 'exist') {
          selectedSegment = selectedSegment ? 'revisit' : 'new'
        }
        return this.initRatio[type].filter(item => item.name === selectedSegment)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
$labelColor: (
  desktop: (
    #0e90a5,
    #fff,
  ),
  tablet: (
    #ffd900,
    #fff,
  ),
  phone: (
    #81bf41,
    #fff,
  ),
  new: (
    #04a6ff,
    #fff,
  ),
  revisit: (
    #004585,
    #fff,
  ),
  "ranking-1": (
    #04a6ff,
    #fff,
  ),
  "ranking-2": (
    #363f86,
    #fff,
  ),
  "ranking-3": (
    #ffd900,
    #f46200,
  ),
  "ranking-4": (
    #9acc67,
    #fff,
  ),
  "ranking-5": (
    #b493ea,
    #fff,
  ),
  "ranking-6": (
    #d7d7d7,
    #666666,
  ),
);

.domain-analysis {
  .error-label {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 8px;
    background-color: #FCE515;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 13px;
    color: #2F2F2F;
    i.icon--bell-till {
      background-color: #fff;
      border-radius: 50%;
    }
    .close-button {
      position: absolute;
      top: 50%;
      right: 8px;
      display: flex;
      transform: translateY(-50%);
      i {
        background-color: transparent;
      }
    }
  }

  .analysis-info {
    @include flexYcenter;
    position: relative;
    min-height: 32px;
    padding: 12px 0;
    .log-date {
      font-size: 12px;
      color: #777;
    }
    .error-label {
      position: absolute;
      top: 11px;
      left: 0;
    }
  }

  .title {
    color: #444;
    margin: 28px 4px 16px;
    font-size: 16px;
    font-weight: 600;
  }

  .traffic-wrap {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 16px;
    .traffic-overview-wrap {
      grid-column: 1 / span 2;
      grid-row: 1 / span 2;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .traffic-detail-wrap {
      display: flex;
      flex-direction: column;
      gap: 16px;
      /deep/ .line_chart > div:first-child {
        width: 100% !important;
        canvas {
          width: 100% !important;
        }
      }
    }

    @media (min-width: 1680px) {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: 2fr 1fr;
      .traffic-overview-wrap {
        grid-row: 1;
        flex-direction: row;
      }
      .traffic-detail-wrap {
        grid-column: 1 / span 3;
        flex-direction: row;
      }
    }
  }
  .flex-equal-wrap {
    position: relative;
    display: flex;
    gap: 16px;
  }
  .box {
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    height: 380px;
    padding: 24px 25px 16px 24px;
    border-radius: 10px;
    background-color: #fff;
    box-sizing: border-box;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.32);

    &.disabled {
      opacity: 0.3;
      pointer-events: none;
    }
    &.short {
      height: 182px;

        &.full-chart {
          justify-content: space-between;
          .box-content {
            padding: 0;
          }
        }
      }
      &.full-chart {
        .box-content {
          padding: 0 12px;
          .chart-info {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 20px 0 30px;
            .total {
              font-size: 40px;
              font-weight: 700;
              color: #444;
            }
            .label {
              position: relative;
              color: #444;
              font-size: 12px;
              &:before {
                position: absolute;
                left: -6px;
                top: 50%;
                transform: translate(-100%, -50%);
                content: '';
                width: 9px;
                height: 3px;
                background-color: #444;
                border-radius: 2px;
              }
            }
          }
        }
        &.session-box {
          .box-title {
            color: #0E90A5;
          }
          .box-content {
            .chart-info {
              .total {
                display: flex;
                align-items: flex-end;
                gap: 8px;
                color: #0E90A5;
                .icon {
                  margin-bottom: 5px;
                }
              }
              .label {
                &:before {
                  background-color: #0E90A5;
                }
              }
            }
          }
        }
      }
      &.report-box {
        position: relative;
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;
        background: linear-gradient(125deg, #253CCD 0%, #1760EC 49.04%, #0074FF 100%);

        .box-title {
          color: #fff;
        }
        .icon--gradient-blue {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }
      &.pv-box {
        gap: 24px;

      .avg-wrap {
        gap: 3px;
        .unit {
          padding-left: 2px;
          color: #767676;
          font-size: 12px;
          font-weight: 600;
        }
        .value-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          .value {
            font-size: 40px;
            font-weight: 600;

            &.dropOff {
              color: #FF4A00;
            }
            &.reload {
              color: #3072F6;
            }
            &.rollback {
              color: #8F67DC;
            }
            &.interval {
              color: #5C8D2C;
            }
          }
        }
      }

      .min-max-wrap {
        display: flex;
        gap: 17px;
        color: #767676;
        font-size: 14px;
        font-weight: 600;
        .label {
          font-size: 12px;
        }
        .max {
          position: relative;
          &::before {
            content: '';
            position: absolute;
            right: -8px;
            top: 50%;
            transform: translateY(-50%);
            width: 1px;
            height: 10px;
            background-color: #ccc;
          }
        }
      }
    }
    &.map-box {
      gap: 26px;
      .mode_selector {
        position: absolute;
        top: 16px;
        right: 16px;
        display: inline-block;
        vertical-align: middle;
        height: 32px;
        border-radius: 16px;
        box-shadow: inset 0 1px 1px 0 rgba(0, 0, 0, 0.04);
        border: solid 1px #d7d7d7;
        background-color: #F4F4F4;

        button {
          display: inline-block;
          box-sizing: border-box;
          line-height: 32px;
          width: 69px;
          height: 32px;
          padding: 0 12px 0 10px;
          cursor: pointer;
          color: #666;
          font-size: 13px;
          font-weight: 500;
          .icon {
            vertical-align: top;
            margin-top: 8px;
            margin-right: 4px;
          }
          &.on {
            border-radius: 16px;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
            background-color: #ffffff;
            color: #444;
            cursor: default;
            &.left {
              margin-right: -7px;
            }
            &.right {
              margin-left: -7px;
            }
          }
        }

      }
      .box-content {
        position: relative;
        .chart-wrap {
          width: calc(100% + 49px);
          margin-left: -24px;
          margin-right: -25px;
          margin-bottom: -16px;

          .zoom-control {
            position: absolute;
            top: 7px;
            left: 0;
            width: 34px;
            height: 34px;

            button {
              background-color: #fff;
              width: 100%;
              height: 100%;
              border-radius: 50%;
              padding-top: 3px;
              margin-bottom: 5px;
              box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.16), 0 6px 10px -3px rgba(0, 0, 0, 0.16);
            }
          }
        }
        .geo-list {
          margin-top: 23px;
          color: #444;
          &-header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 0 12px 8px 12px;
            border-bottom: 1px solid #BBB;
            font-size: 12px;
          }
          &-body {
            display: flex;
            flex-direction: column;
            .item {
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 8px 12px;
              border-bottom: 1px dashed #D7D7D7;
              font-size: 13px;
              &:last-child {
                border-bottom-style: solid;
              }
            }
          }
          .rank {
            width: 28px;
            text-align: center;
          }
          .flag {
            width: 24px;
            img {
              width: 24px;
              height: 24px;
            }
          }
          .label {
            flex: 1;
          }
          .no-data {
            width: 100%;
            margin-top: 117px;
            text-align: center;
            color: #444;
            font-size: 13px;
          }
        }
      }
      .error-label {
        position: absolute;
        bottom: 0;
        left: 0;
        width: calc(100% - 16px);
        padding: 4px;
        margin: 0 8px 8px;
        font-size: 12px;
      }
    }
    &.refer-box {
      margin-top: 16px;
      gap: 24px;
      .box-content {
        display: flex;
        gap: 24px;
        .pie_chart_container {
          margin-top: 12px;
        }
        .top {
          width: 310px;
          flex-shrink: 0;
          margin-right: 13px;
          .top-header {
            .rank {
              width: 38px;
              text-align: center;
            }
            .category {
              padding-right: 36px;
            }
          }
          .top-list {
            .item {
              padding: 11px 8px 11px 46px;
              cursor: pointer;
              &-label {
                max-width: 190px;
              }
              &-value-wrap {
                display: flex;
                align-items: center;
                gap: 15px;
                .icon--arrow-body-dark-gray {
                  opacity: 0;
                }
              }
              &:hover:not(.selected) {
                background: rgba(0, 0, 0, 0.06);
                .icon--arrow-body-dark-gray {
                  opacity: 1;
                }
              }

              &.selected {
                position: relative;
                &:after {
                  content: '';
                  position: absolute;
                  left: -2px;
                  top: -2px;
                  width: calc(100% + 4px);
                  height: calc(100% + 4px);
                  border: 2px solid red;
                  border-radius: 4px;
                  box-sizing: border-box;
                  box-shadow: 0 8px 10px 0 rgba(0, 0, 0, 0.15);
                }
              }
              &::before {
                content: attr(data-ranking);
                @include flexXYcenter;
                left: 12px;
                width: 14px;
                height: 14px;
                font-size: 10px;
                font-weight: 400;
                line-height: 1;
              }
              &.etc {
                color: #777;
                &::before {
                  content: '';
                  background-image: url(../../assets/img/dashboard/icon--etc-mark.svg);
                  background-color: transparent;
                }
              }
            }
          }
          &.segmented {
            margin-top: 19px;
            margin-right: 0;
            .top-header {
              .rank {
                display: none;
              }
            }
            .top-list {
              .item {
                padding-left: 8px;
                &-label {
                  max-width: 225px;
                }

                &::before {
                  content: none;
                }
              }
            }
          }
        }
        /deep/ .line_chart {
          margin-top: 22px;
        }
      }
    }
    &.pv-top-list-box {
      margin-top: 16px;
      gap: 17px;
    }

    &.trend-box {
      gap: 20px;
      padding-bottom: 32px;
    }
    &-title {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #444;
      font-size: 14px;
      font-weight: 600;
    }

    &-content {
      .chart-with-top {
        display: flex;
        gap: 24px;
        margin: 24px 0 28px;
      }

      .pie-chart-wrap {
        flex-shrink: 0;
        position: relative;
        margin-top: 4px;
        width: 124px;
        height: 124px;
        .pie_chart_container {
          position: absolute;
          top: 0;
          left: 0;
        }
      }

      .top {
        width: 100%;
        .top-header {
          display: flex;
          border-bottom: 1px solid #BBB;
          color: #777;
          font-size: 12px;
          font-weight: 500;
          .rank {
            width: 18px;
            text-align: center;
            padding: 8px 4px;
            box-sizing: border-box;
          }
          .category {
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding: 8px;
            box-sizing: border-box;
          }
        }
        .top-list {
          .item {
            position: relative;
            display: flex;
            justify-content: space-between;
            padding: 8px 8px 8px 26px;
            color: #444;
            font-size: 13px;
            font-weight: 400;
            line-height: 120%;
            text-overflow: ellipsis;
            &:not(:last-child) {
              border-bottom: 1px solid #EBEBEB;
            }
            &::before {
              content: '';
              position: absolute;
              left: 4px;
              top: 50%;
              transform: translateY(-50%);
              width: 10px;
              height: 10px;
              border-radius: 50%;
            }
            &-label {
              max-width: 110px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
        .no-data {
          margin-top: 112px;
          color: #999;
          font-size: 12px;
          font-weight: 500;
          text-align: center;
        }
      }

      .yLabel {
        color: #666;
        font-size: 10px;
      }
    }
    .line-chart-wrap {
      width: calc(100% + 25px);
      margin: 0 -15px 0 -10px;
    }
    .avg {
      position: absolute;
      top: 24px;
      right: 24px;
      display: flex;
      align-items: flex-end;
      gap: 8px;
      &-value {
        color: #444;
        font-size: 22px;
        font-weight: 600;
      }
      &-unit {
        padding-bottom: 3px;
        color: #777;
        font-size: 12px;
        font-weight: 500;
      }
    }
    .loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  @each $label, $value in $labelColor {
    $bg: nth($value, 1);
    $fontColor: nth($value, 2);
    .top-list {
      .item {
        &.#{$label} {
          &::before {
            background-color: $bg;
          }
          &::before {
            color: $fontColor;
          }
        }
      }
    }
  }
}
</style>
